import React, { useCallback, useMemo, useState } from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Button, Checkbox } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { debounce } from "../../../../../utils";
import "../../../../Admin/Reports/Reports Detailed/ReportsDetailed.css";
import { BreadcrumbAdmin } from "../../../../UI/BreadcrumbAdmin";
import DateRangePickerComp from "../../../../UI/DateRangePicker";
import { Search } from "../../../../UI/Search/Search";
import { useOutsideClick } from "../../../../../hooks/useOutsideClick";
import { useGetAdminActivityLogMetaQuery } from "../../../../../redux-toolkit/features/adminActivityLog/adminActivityLogApiSlice";
import { AdminActivityLogTable } from "./AdminActivityLogTable";
import { FilterMultiSelect } from "./FilterMultiSelect";

const list = [
  { name: "rowNum", label: "#" },
  { name: "createdAt", label: "Date/Time" },
  { name: "actor", label: "Actor" },
  { name: "role", label: "Role" },
  { name: "action", label: "Action" },
  { name: "entityType", label: "Entity Type" },
  { name: "entity", label: "Entity" },
  { name: "company", label: "Company" },
];

const padDateUnit = (value) => String(value).padStart(2, "0");

const toIsoDatePart = (date) => {
  const selectedDate = new Date(date);
  const year = selectedDate.getFullYear();
  const month = padDateUnit(selectedDate.getMonth() + 1);
  const day = padDateUnit(selectedDate.getDate());
  return `${year}-${month}-${day}`;
};

const toUtcDayStartISOString = (date) => `${toIsoDatePart(date)}T00:00:00.000Z`;
const toUtcDayEndISOString = (date) => `${toIsoDatePart(date)}T23:59:59.999Z`;

const formatActionLabel = (action, fallback = "") => {
  const fromAction = String(action || "")
    .split(".")
    .filter(Boolean)
    .map((part) => part.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()))
    .join(" ");
  const raw = fromAction || String(fallback || "");
  return raw
    .replace(/\u2013|\u2014/g, " ")
    .replace(/\s*[-–—]\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const AdminActivityLog = () => {
  const [select, setSelect] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [actionFilter, setActionFilter] = useState([]);
  const [entityTypeFilter, setEntityTypeFilter] = useState([]);
  const ref = React.useRef(null);
  const { data: metaResponse } = useGetAdminActivityLogMetaQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const actionLabels = metaResponse?.data?.actionLabels ?? {};
  const actionsByEntityType = metaResponse?.data?.actionsByEntityType ?? {};
  const entityTypeOptions = (metaResponse?.data?.entityTypes ?? []).map((value) => ({
    value,
    label: value,
  }));

  const allActionOptions = useMemo(
    () =>
      Object.entries(actionLabels)
        .map(([value, label]) => ({
          value,
          label: formatActionLabel(value, label),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [actionLabels]
  );

  const actionOptions = useMemo(() => {
    if (!entityTypeFilter.length) return allActionOptions;
    const allowed = new Set();
    for (const entity of entityTypeFilter) {
      (actionsByEntityType[entity] || []).forEach((action) => allowed.add(action));
    }
    return allActionOptions.filter((opt) => allowed.has(opt.value));
  }, [allActionOptions, entityTypeFilter, actionsByEntityType]);

  const handleEntityTypeChange = useCallback(
    (nextEntities) => {
      setEntityTypeFilter(nextEntities);
      if (!nextEntities.length) return;
      const allowed = new Set();
      for (const entity of nextEntities) {
        (actionsByEntityType[entity] || []).forEach((action) => allowed.add(action));
      }
      setActionFilter((prev) => prev.filter((action) => allowed.has(action)));
    },
    [actionsByEntityType]
  );

  const closePopup = () => setSelect(false);
  useOutsideClick(ref, closePopup);

  const [check, setCheck] = useState({
    rowNum: true,
    createdAt: true,
    actor: true,
    role: true,
    action: true,
    entityType: true,
    entity: true,
    company: true,
  });

  const base = [];

  const setDebouncedSearchQuery = useCallback(
    debounce((value) => {
      setSearchQuery(value);
    }, 500),
    []
  );

  const handleChange = (e) => {
    setCheck({ ...check, [e.target.name]: e.target.checked });
  };

  return (
    <div className="container">
      <BreadcrumbAdmin base={base} page={"Activity Logs"} />
      <p
        style={{
          marginTop: 0,
          marginBottom: "16px",
          color: "var(--grey500, #666)",
          fontSize: "14px",
        }}
      >
        Audit trail of privileged actions by admins and superadmins across the
        organization.
      </p>
      <div className="reportsDetailed__head">
        <Search
          mini
          setSearchVal={(val) => setDebouncedSearchQuery(val)}
          placehold={"Search by actor, entity, company..."}
        />
        <div className="reportsDetailed__headright">
          <FilterMultiSelect
            label="Entity"
            options={entityTypeOptions}
            value={entityTypeFilter}
            onChange={handleEntityTypeChange}
            minWidth={180}
          />
          <FilterMultiSelect
            label="Action"
            options={actionOptions}
            value={actionFilter}
            onChange={setActionFilter}
            minWidth={200}
          />
          <DateRangePickerComp
            onChange={(value) => {
              if (!value) {
                setDateRange(null);
              } else {
                const [start, end] = value;
                if (!start || !end) {
                  setDateRange(null);
                  return;
                }
                setDateRange([
                  toUtcDayStartISOString(start),
                  toUtcDayEndISOString(end),
                ]);
              }
            }}
          />
          <div ref={ref} className="reports__selectLayout">
            <Button
              variant="outlined"
              endIcon={
                <ArrowForwardIosIcon
                  sx={{
                    transform: select ? "rotate(-90deg)" : "rotate(90deg)",
                    color: "grey",
                    fontSize: "16px !important",
                  }}
                />
              }
              sx={{
                color: "var(--grey400)",
                borderColor: "var(--grey300)",
                width: "150px",
                fontWeight: "400 !important",
              }}
              onClick={() => setSelect(!select)}
            >
              Manage Field
            </Button>
            <div className={select ? "reports__selectActive" : "reports__select"}>
              <div className="reports__selectcontent">
                {list.map((item, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={Boolean(check[item.name])}
                        name={item.name}
                        onChange={handleChange}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: "20px" } }}
                      />
                    }
                    label={
                      <span style={{ fontSize: "15px" }}>{item.label}</span>
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="reports__table">
        <AdminActivityLogTable
          check={check}
          searchQuery={searchQuery}
          startDate={dateRange && dateRange[0]}
          endDate={dateRange && dateRange[1]}
          actionFilter={actionFilter}
          entityTypeFilter={entityTypeFilter}
          actionLabels={actionLabels}
        />
      </div>
    </div>
  );
};
